/* eslint-disable @typescript-eslint/no-unused-vars */
import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccountModel, AccountSearchListModel, AgentsClient, InvestmentAccountsClient, OrionEclipseClient } from 'src/app/core/clients/generated/client';
import { ToastClassEnum } from 'src/app/core/services/snackbar/snackbar.models';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { fadeIn } from 'src/app/shared/constants/animations';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalData } from 'src/app/shared/modals/confirmation-modal/modal-data.models';
import { Account } from '../../../accounts/models/account.models';
import { RequestTypeEnum } from 'src/app/portal/features/account-request-workflows/shared/enums/account-update.enum';
import { AccountWorkflowNavModel, SolicitationOptionModel } from 'src/app/portal/features/account-request-workflows/shared/models/workflow.models';
import { WorkflowNavigationService } from 'src/app/portal/features/account-request-workflows/shared/services/workflow-navigation.service';
import { catchError, debounceTime, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { Permission } from 'src/app/core/clients/generated/client';
import { PermissionsService } from 'src/app/core/auth/permissions.service';
import { isInitialAccountEligible } from './initial-account-eligibility';
import { deriveTradeRestrictionMessage } from './trade-restriction';

@Component({
  animations: [fadeIn],
  selector: 'app-account-update-main-modal',
  templateUrl: './account-update-main-modal.component.html',
})
export class AccountUpdateMainModalComponent implements OnInit, AfterViewInit {
  perm = Permission;
  accounts: AccountSearchListModel[] = [];
  advisorIds: number[] = [];
  filteredAccounts!: AccountSearchListModel[];
  searchLoading = true;
  modalLoading = false;
  requestTypeEnum = RequestTypeEnum;
  searchField = this.fb.group({
    searchInput: this.fb.nonNullable.control<string>('')
  });
  selectedAccountId?: number;
  selectedAccountValue?: number;
  selectedAccountStatus?: string;
  account?: AccountModel;
  selectedWorkflow: RequestTypeEnum = this.requestTypeEnum.accountSelection;
  workflowNavItems: AccountWorkflowNavModel[] = [];
  selectedWorkflowNavItem: AccountWorkflowNavModel | undefined = undefined;
  /**
   * 🔴 ALWAYS UNDEFINED, and kept only so the `[solicitation]` bindings still compile.
   *
   * The step that set it is gone (AndyP, 2026-10-02) and nothing assigns it any more. The child
   * wizards each guard on it being present, so they simply never read a solicitation — which is
   * the point: `isSolicited` is filed as NULL now, on every request type.
   *
   * ⚠️ Left in place rather than ripped out of five child components for a field that is on
   * its way to being deleted outright. If this is still here when someone next touches those
   * inputs, remove the whole chain.
   */
  selectedSolicitationOption: SolicitationOptionModel | undefined = undefined;
  // 🔴 `pickerStep` LIVED HERE AND IS GONE (AndyP, 2026-10-02). The picker had a second step
  // asking who initiated the request; it no longer asks, so there is one screen and nothing to
  // track. Every "send it back to step 1" guard went with it -- `resetEvent`, `resetSearch` and
  // `applyInitialAccountEligibility` each carried one, and each existed only so step 2 could not
  // be left summarising a request that had just been withdrawn.
  /**
   * Whether the selected account looks like it has never been set up, so an Initial Account
   * Request is the right thing to steer the advisor toward. `undefined` until the Eclipse
   * lookup resolves, which keeps the option disabled rather than briefly enabled.
   */
  initialAccountEligible?: boolean;
  /**
   * True from the moment an account is chosen until we know which request types apply to it.
   * The request list is replaced by a loader meanwhile, so options don't render dimmed and then
   * light up a moment later. Tracked explicitly rather than inferred from
   * `initialAccountEligible === undefined`, so the two can move independently.
   */
  eligibilityLoading = false;
  /**
   * The Eclipse lookup failed, so eligibility is UNKNOWN - not false.
   *
   * `initialAccountEligible` deliberately stays `undefined` in this state: "not eligible" is a
   * claim about the account, and a failed lookup has not earned it. Asserting it anyway used to
   * hide Initial Account AND, because `standardNavItems` only withholds the V2 reallocation tile
   * when Initial Account is recommended, reveal Reallocation on an account that qualified - so
   * the advisor filed the wrong request TYPE (type 2 into TaskCategory 11, not type 1 into 25).
   *
   * The menu now offers both and lets the wizard decide. Each wizard does its own account
   * lookup and reports its own failure - the V2 one throws AccountPositionsNotFoundError when
   * Eclipse has no record and renders it inline plus a toast - so the authoritative check
   * already lives where the data is needed. Duplicating it here bought nothing. ACMTECH-935.
   */
  eligibilityUnknown = false;
  /**
   * The trade-restriction warning for the chosen account, or null when there is nothing to say —
   * either it is unrestricted, or the Eclipse lookup failed and we will not guess.
   *
   * Shown in the footer beside Next, for every request type, because a restriction is a fact about
   * the account rather than about the request. Advisory only: the advisor may still submit.
   */
  tradeRestrictionMessage: string | null = null;
  private noAccountValueMessage = 'You can not perform this type of request on an account with a value of $0. Please contact Alphastar for additional support.';
  private closedAccountMessage = 'You can not perform this type of request on an account that has been closed. Please contact Alphastar for additional support.';
  private errorMessage = 'An error occurred retrieving your account. Please try again.';

  constructor(
    private dialog: MatDialog,
    private agentsApiService: AgentsClient,
    public dialogRef: MatDialogRef<AccountUpdateMainModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Account,
    private fb: FormBuilder,
    private investmentAccountApi: InvestmentAccountsClient,
    private orionEclipseApi: OrionEclipseClient,
    private snackbarService: SnackbarService,
    private _workflowNavigationService: WorkflowNavigationService,
    private _permissionService: PermissionsService,
  ) { }

  ngOnInit(): void {
    // Don't let a stray backdrop click / Esc close the modal and lose an
    // in-progress request; the modal is closed deliberately via its X button.
    this.dialogRef.disableClose = true;
    this.modalLoading = true;
    this.workflowNavItems = this._workflowNavigationService.workflowNavList;

    if (this._permissionService.hasOnePermission(Permission.ViewOrionElementRequests)) {
      this.selectedWorkflowNavItem = this.workflowNavItems.find(x => x.order === RequestTypeEnum.reallocation);
    }

    // 🔴 NO SOLICITATION DEFAULT IS SET ANY MORE. This used to preselect the IAR-initiated
    // entry so step 2 opened with an answer already in place. There is no step 2 and no answer:
    // `isSolicited` reaches CRM as NULL. `_workflowSilicationOptionsList` on the navigation
    // service is now unread by this modal and is left alone -- it is v1 shared state.
    this.agentsApiService.getParentAgents(true)
      .subscribe({
        next: (results) => {
          this.advisorIds = results.map(advisor => {
            return advisor.agentID;
          });
        },
        error: () => {
          this.snackbarService.openSnackbar('There was an error loading advisors', ToastClassEnum.warning);
        },
        complete: () => {
          if (this.data) this.prefillSearchInput(this.data);
          this.modalLoading = false;
        }
      });

    this.searchField.controls.searchInput.valueChanges.pipe(
      filter(searchText => !!searchText && searchText.length > 2),
      tap(() => this.searchLoading = true), //set loading to true as soon as the filter criteria are satisfied
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((searchText: string, index: number) => {
        return this.investmentAccountApi.searchAcmAccountsForAgentIds(this.advisorIds, searchText); //only complete search if input hasn't changed to prevent unnecessary api calls
      }))
      .subscribe({
        next: (results: AccountSearchListModel[]) => {
          this.accounts = results;
          this.searchLoading = false;
        },
        error: () => {
          this.searchLoading;
          this.snackbarService.openSnackbar('Error retrieving Accounts.', ToastClassEnum.warning);
        }
      });
  }

  ngAfterViewInit(): void {
    const width = '80rem';
    const height = '84rem';
    this.dialogRef.updateSize(width, height);
  }

  /** Prefill search input with data from selected account from account list
   * @param account
  */
  prefillSearchInput(account: Account): void {
    if (account.InvestmentAccountId) {
      const accountNumberString = account.AccountNumber ?? 'No Account Number';
      this.selectedAccountId = account.InvestmentAccountId;
      this.selectedAccountValue = account.Value ?? undefined;
      this.selectedAccountStatus = account.Status ?? undefined;
      this.searchField.controls.searchInput.setValue(`${account.Custodian} - ${accountNumberString} - ${account.Type}`);
      this.loadEclipseAccountContext(account.InvestmentAccountId);
    }
  }

  /**
   * Read the account's Orion Eclipse context and derive the two things this screen needs from it:
   * whether the account qualifies for an Initial Account Request, and whether it is trade
   * restricted.
   *
   * Eligibility: see {@link isInitialAccountEligible}, which owns the rule and is where its
   * reasoning lives. In short, a single row carrying no model at all -- anything else has either
   * been sleeved or been assigned something, and wants a Model Allocation / Trade Request instead.
   * A failed lookup counts as not eligible, so we never recommend on unverified data.
   *
   * Restriction: the portfolio-wide block, or any one restricted sleeve. Warning only -- the
   * advisor may still submit (ACMTECH-904).
   *
   * NOT permission-gated, deliberately. It used to skip entirely without
   * SubmitReallocationV2Request, back when eligibility was all it produced. The restriction warning
   * applies to every request type, so gating this would mean the same restricted account warns one
   * advisor and not another -- which makes the warning read as unreliable rather than absent.
   * Computing eligibility for everyone is harmless: the tile it drives is permission-gated anyway.
   *
   * One subscription for both calls, so eligibilityLoading spans the pair and the request list does
   * not flash between them. The portfolio read needs a portfolioId, which only the sleeve rows
   * supply -- hence chained rather than parallel.
   */
  private loadEclipseAccountContext(accountId: number): void {
    this.initialAccountEligible = undefined;
    this.eligibilityUnknown = false;
    this.tradeRestrictionMessage = null;
    this.eligibilityLoading = true;

    this.orionEclipseApi.getAccountDetails(accountId).pipe(
      switchMap((sleeves) => {
        const portfolioId = sleeves?.find((s) => s.portfolioId != null)?.portfolioId;
        if (portfolioId == null) return of({ sleeves, portfolio: null });
        return this.orionEclipseApi.getPortfolio(portfolioId, accountId).pipe(
          // A readable account with an unreadable portfolio is still worth showing sleeve-level
          // restriction for, so this failure degrades rather than losing the rows as well.
          catchError(() => of(null)),
          map((portfolio) => ({ sleeves, portfolio })),
        );
      }),
    ).subscribe({
      next: ({ sleeves, portfolio }) => {
        this.applyInitialAccountEligibility(isInitialAccountEligible(sleeves));
        this.tradeRestrictionMessage = deriveTradeRestrictionMessage(sleeves, portfolio);
      },
      error: () => {
        // Not applyInitialAccountEligibility(false) - see eligibilityUnknown. Nothing is
        // withheld in this state, so no selection can go stale and there is nothing to clear.
        this.eligibilityUnknown = true;
        // Absent, not "unrestricted" -- a warning that silently disappears on a failed lookup is
        // worse than no warning, because a clean footer reads as a clean account.
        this.tradeRestrictionMessage = null;
      },
    }).add(() => this.eligibilityLoading = false);
  }

  private applyInitialAccountEligibility(eligible: boolean): void {
    this.initialAccountEligible = eligible;
    // Changing accounts can disqualify an option the advisor already picked; don't leave it selected.
    if (!eligible && this.selectedWorkflowNavItem?.order === RequestTypeEnum.initialAccountSetup)
      this.selectedWorkflowNavItem = undefined;

    // The mirror case, and it is reachable without a click: ngOnInit preselects the v1 reallocation
    // item for anyone holding ViewOrionElementRequests, before an account is chosen and so before
    // eligibility is known.
    //
    // Only a V2 selection is cleared. v1 stays in the main list on a qualifying account (see
    // standardNavItems), so its tile is still on screen and clearing the advisor's pick would take
    // away a choice they can still make. V2's tile does disappear, and Next acts on the SELECTION
    // rather than on what is rendered, so leaving that one set would open a wizard with no tile.
    //
    // Cleared rather than switched to Initial Account: substituting a choice the advisor did not
    // make is worse than asking them to pick.
    if (this.recommendedNavItems.length
      && this.selectedWorkflowNavItem?.order === RequestTypeEnum.reallocationV2)
      this.selectedWorkflowNavItem = undefined;

  }

  /** Initial Account Setup, promoted out of the main list when the account qualifies for it. */
  get recommendedNavItems(): AccountWorkflowNavModel[] {
    if (!this.initialAccountEligible) return [];
    return this.workflowNavItems.filter(x => x.order === RequestTypeEnum.initialAccountSetup);
  }

  /**
   * The request types in the main list.
   *
   * Initial Account Setup is never here: it is either promoted to Recommended, or the
   * account does not qualify for it and it is not offered at all. Offering a request the
   * advisor cannot file — dimmed, with a tooltip explaining why — is an invitation to
   * click something that does nothing, and the account they are on is what decides it.
   *
   * ⚠️ When Initial Account Setup IS offered, the **V2** Model Allocation / Trade Request is
   * withheld — and only that one. Initial Account and V2 open the same wizard (the modal
   * renders `<app-reallocation-v2-request>` for either), so offering both is two routes to one
   * place, and on a qualifying account the Initial Account entry point is the right one. It also
   * means such an account has no route to the Buy step, which is deliberate: buys are excluded
   * from a type-1 request (see `steps` in reallocation-v2.store), so the V2 tile was the only way
   * around a rule the CRM's type-1 screens depend on.
   *
   * ✅ **v1 stays in the list.** It opens `<app-reallocation-request>` — a different workflow
   * writing the legacy tables, not a second door to the same wizard — so none of the reasoning
   * above applies to it. Withholding it left a pilot user with no way to file an ordinary
   * reallocation against a qualifying account, which is the fallback the pilot depends on.
   *
   * Keyed off `recommendedNavItems` rather than re-testing the permission and eligibility
   * separately, so "is the tile showing" has exactly one definition.
   */
  get standardNavItems(): AccountWorkflowNavModel[] {
    const hideReallocationV2 = this.recommendedNavItems.length > 0;
    // When the lookup failed we cannot say whether Initial Account applies, so it appears in the
    // main list alongside the others rather than being withheld. V2 needs no extra condition:
    // recommendedNavItems is empty in that state, so hideReallocationV2 is already false.
    return this.workflowNavItems.filter(x =>
      (x.order !== RequestTypeEnum.initialAccountSetup || this.eligibilityUnknown)
      && !(hideReallocationV2 && x.order === RequestTypeEnum.reallocationV2));
  }

  /**
   * The V2 request types, promoted to the top of the main list.
   *
   * Split on the existing `isNew` flag rather than on a list of enum members, so a tile joins or
   * leaves this group by the same property that gives it its badge — the two cannot disagree.
   *
   * ⚠️ Initial Account Setup lands here when the eligibility lookup FAILED (see
   * `standardNavItems`), which is right: it is a V2 wizard, and it belongs beside the others
   * rather than buried among the classic requests.
   */
  get newNavItems(): AccountWorkflowNavModel[] {
    return this.standardNavItems.filter(x => x.isNew);
  }

  /**
   * Everything else — the v1 workflows, plus Close the Account and General Account Message,
   * which have no V2 counterpart.
   *
   * 🟢 The grouping is what tells an advisor that CLOSE THE ACCOUNT is not the current path for
   * closing an account. Once V2 distribution is granted, closing happens inside
   * `DISTRIBUTION/CLOSE ACCOUNT V2`, and the menu had no way of saying so.
   *
   * That claim is now carried by the panel around the group above rather than by a label on this
   * one, which is weaker: it says "those are the new ones" and leaves "so these are not" to be
   * inferred. Deliberate — a "Classic requests" heading read as a demotion of the two members
   * that have no V2 counterpart and are not superseded by anything.
   */
  get classicNavItems(): AccountWorkflowNavModel[] {
    return this.standardNavItems.filter(x => !x.isNew);
  }

  /**
   * Whether to present the list as two groups at all: the NEW EXPERIENCE heading, its badge, the
   * mint panel drawn around the V2 rows, and the classic group's top margin.
   *
   * Only ONE heading is rendered: the panel draws the boundary, so a second label over the
   * classic rows was naming something the container had already said. The name stays plural
   * because the flag governs that whole presentation rather than just the one heading.
   *
   * 🔴 Only when BOTH have contents. An advisor holding no V2 permissions sees exactly today's
   * flat list with no heading, and — the point worth keeping — so does everyone once v1 is
   * retired and the classic group empties. The grouping is scaffolding for the pilot and takes
   * itself down when the pilot ends, rather than leaving a lone "NEW EXPERIENCE" heading over
   * every request in the menu.
   *
   * ⚠️ It also gates the per-row `isNew` badges, which appear only in its absence. With no
   * classic rows left to contrast against, the mint fill alone does not say "new" — so the
   * badge has to come back onto the row.
   */
  get showNavGroupHeadings(): boolean {
    return this.newNavItems.length > 0 && this.classicNavItems.length > 0;
  }

  /**
   * These buttons are dimmed with a class rather than the disabled attribute, so they stay
   * clickable -- every handler that acts on a nav item has to consult this as well.
   *
   * Nothing is disabled per request type any more: an account either has none selected, in
   * which case every tile is dimmed, or a tile is offered and is fileable. Initial Account
   * eligibility is handled by not rendering that tile at all (see recommendedNavItems).
   *
   * `navItem` is kept in the signature for the call sites, and because a per-type rule
   * returning here is the likely reason anyone reads this again.
   */
  isNavItemDisabled(_navItem: AccountWorkflowNavModel): boolean {
    return !this.selectedAccountId;
  }

  navItemTooltip(navItem: AccountWorkflowNavModel): string {
    return `Click to go to ${navItem.title.toLowerCase()}`;
  }

  /**
   * Choose a request type, and open its workflow.
   *
   * No Next: the list is a set of choices, not a form field to fill and confirm, so a click IS
   * the answer.
   *
   * 🔴 EVERY TYPE OPENS IMMEDIATELY NOW (AndyP, 2026-10-02). This used to send everything but
   * a General Account Message to a second step asking who initiated the request. That question
   * is no longer asked and `isSolicited` is filed as NULL, so the General Account special case
   * disappeared too -- it was only ever "this one has no solicitation to collect", which is now
   * true of all of them.
   *
   * 🟢 The second argument stays in the signature. `selectNavItem` is the one entry point into a
   * workflow and takes the solicitation it should pass down; passing `undefined` says plainly
   * that there is none, where dropping the parameter would hide that a decision was made.
   */
  chooseNavItem(navItem: AccountWorkflowNavModel): void {
    if (this.isNavItemDisabled(navItem)) return;

    this.selectedWorkflowNavItem = navItem;
    this.selectNavItem(navItem, undefined);
  }

  // 🔴 `backToRequestTypes` LIVED HERE AND IS GONE. It was the second step's Back button, and
  // there is no second step to come back from.

  resetEvent(): void {
    if (this.selectedWorkflow !== this.requestTypeEnum.accountSelection) {
      this.selectedWorkflow = this.requestTypeEnum.accountSelection;
    }
  }

  /**clear search field or reset search results when an account is selected */
  resetSearch(): void {
    this.searchField.controls.searchInput.reset();
    this.selectedAccountId = undefined;
    this.selectedAccountValue = undefined;
    this.initialAccountEligible = undefined;
    this.tradeRestrictionMessage = null;
    this.eligibilityLoading = false;
  }

  selectAccount(account: AccountSearchListModel): void {
    // Covers both round-trips, so the request list doesn't flash between the account load and
    // the eligibility check that follows it.
    this.eligibilityLoading = true;

    this.investmentAccountApi.getInvestmentAccount(account.investmentAccountID)
      .subscribe({
        next: (account) => {
          this.selectedAccountId = account.investmentAccountID;
          this.selectedAccountValue = account.accountValue;
          this.selectedAccountStatus = account.investmentAccountStatus;
          this.loadEclipseAccountContext(account.investmentAccountID);
        },
        error: () => {
          // not expected to happen - only on random network errors
          this.eligibilityLoading = false;
          // Clear both, or they persist from whichever account was selected before this one and
          // describe the wrong account.
          this.initialAccountEligible = undefined;
          this.tradeRestrictionMessage = null;
          this.snackbarService.openSnackbar('There was an error processing your request.', ToastClassEnum.warning);
        }
      });
  }

  selectNavItem(navItem: AccountWorkflowNavModel | undefined, solicitationItem: SolicitationOptionModel | undefined): void {

    if (!this.selectedAccountId) return;

    if (navItem === undefined) {
      if (!this.isGeneralRequest){
        if (solicitationItem === undefined)
          return;
      }
      return;
    }

    // The nav buttons are only dimmed by class, so re-check before acting on the selection.
    if (this.isNavItemDisabled(navItem)) return;

    if (this.accountIsClosed()) {
      this.openConfirmationModal(this.closedAccountMessage, 'Okay', false);
      return;
    }

    if (this.isDistributionOrReallocationRequest(navItem) && !this.hasAccountValue()) {
      this.openConfirmationModal(this.noAccountValueMessage, 'Okay', false);
      return;
    }

    this.modalLoading = true;
    this.selectedWorkflowNavItem = navItem;
    this.selectedSolicitationOption = solicitationItem;
    this.selectedWorkflow = navItem.order;
    this.investmentAccountApi.getInvestmentAccount(this.selectedAccountId)
      .subscribe({
        next: (account) => {
          this.account = account;
        },
        error: () => {
          this.openConfirmationModal(this.errorMessage, 'Close', true);
          this.selectedWorkflow = this.requestTypeEnum.accountSelection;
        }
      }).add(() => this.modalLoading = false);
  }

  openConfirmationModal(message: string, buttonText: string, isWarning: boolean): void {
    this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      data: {
        message: message,
        confirmationButtonText: buttonText,
        isWarning: isWarning,
        hideCancelButton: true
      }
    });
  }

  /**
   * Request types that need the account to already hold something.
   *
   * ⚠️ `contributionV2` is deliberately absent. A contribution ADDS money, so an empty account
   * is not merely allowed but the likeliest case — blocking it would refuse the one request
   * that fixes the condition being complained about.
   */
  private isDistributionOrReallocationRequest = (navItem: AccountWorkflowNavModel): boolean => [RequestTypeEnum.distribution, RequestTypeEnum.distributionV2, RequestTypeEnum.reallocation, RequestTypeEnum.reallocationV2].includes(navItem.order);
  private isGeneralRequest = (navItem: AccountWorkflowNavModel): boolean => [RequestTypeEnum.generalAccount].includes(navItem.order);
  private hasAccountValue = (): boolean => !!this.selectedAccountValue;
  private accountIsClosed = (): boolean => this.selectedAccountStatus === 'Closed';
}
